import { POS_BASE, PosApiError } from "@/lib/api/client";
import type { Problem } from "@/lib/api/types";

/**
 * Staff-PIN calls. The switch itself posts to `/pos-auth/pin-switch` — the till's own
 * session cookie, never a request field, decides which till it is checked against, and a right
 * PIN re-sets that cookie server-side. Set/unlock stay on the POS edge (`/pos/staff/pin*`): they
 * never mint a session, so there is no cookie for them to carry.
 */

/** The five shapes a PIN attempt can answer with — never a raw contract code at the till. */
export type PinOutcome = "SWITCHED" | "WRONG_PIN" | "LOCKED" | "NOT_ENROLLED" | "LOCATION_DENIED";

export type PinAttemptResult = { outcome: PinOutcome; attemptsRemaining: number | null };

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let problem: Problem;
    try {
      problem = (await res.json()) as Problem;
    } catch {
      problem = { code: `HTTP_${res.status}`, message: res.statusText };
    }
    throw new PosApiError(res.status, problem);
  }
  return (await res.json().catch(() => null)) as T;
}

/**
 * Switches this till's session into a colleague proven by their own PIN. `staffUserId` is the
 * colleague's own login id (`PosStaff.userId`, not the staff-directory id) — the same id the till's
 * session already carries for whoever is signed in.
 */
export async function switchStaffPin(staffUserId: string, pin: string): Promise<PinAttemptResult> {
  // The switch relays User's own answer, which wraps the outcome in `data`; unlock answers it bare.
  const answer = await post<{ data: PinAttemptResult }>("/pos-auth/pin-switch", { staffUserId, pin });
  return answer.data;
}

/** Sets or replaces the CALLER's own PIN — never another staff member's. */
export function setOwnPin(pin: string): Promise<void> {
  return post<void>(`${POS_BASE}/staff/pin`, { pin });
}

/**
 * A manager clears a colleague's lockout at this till, proven by the manager's own PIN. Its own
 * outcome shape: a success reads `UNLOCKED_BY_MANAGER`, never `SWITCHED` — this never mints a
 * session, it only clears one colleague's failure count so their own PIN works again.
 */
export type UnlockResult = { outcome: "UNLOCKED_BY_MANAGER" | "WRONG_PIN" | "LOCKED" | "NOT_ENROLLED"; attemptsRemaining: number | null };

export function unlockStaffPin(
  managerStaffUserId: string,
  managerPin: string,
  staffUserId: string,
): Promise<UnlockResult> {
  return post<UnlockResult>(`${POS_BASE}/staff/pin/unlock`, {
    managerStaffUserId,
    managerPin,
    staffUserId,
  });
}
